import React from "react";
import canopen from "../stack/canopen";

// CRC32 calculation (standard polynomial 0xEDB88320)
function crc32(data) {
	let crc = 0xFFFFFFFF;
	for (let i = 0; i < data.length; i++) {
		crc ^= data[i];
		for (let j = 0; j < 8; j++) {
			crc = (crc >>> 1) ^ (0xEDB88320 & -(crc & 1));
		}
	}
	return (crc ^ 0xFFFFFFFF) >>> 0;
}

export default function FirmwareFlasher() {
	const [nodeId, setNodeId] = React.useState(1);
	const [firmwareFile, setFirmwareFile] = React.useState(null);
	const [firmwareData, setFirmwareData] = React.useState(null);
	const [calculatedCRC, setCalculatedCRC] = React.useState(null);
	const [busy, setBusy] = React.useState(false);
	const [status, setStatus] = React.useState("");
	const [error, setError] = React.useState("");
	const [flashComplete, setFlashComplete] = React.useState(false);
	const [showConfirmDialog, setShowConfirmDialog] = React.useState(false);

	const handleFileSelect = (e) => {
		const file = e.target.files[0];
		if (!file) return;

		if (file.size > 64 * 1024) {
			setError("Firmware file too large (max 64KB)");
			return;
		}

		setFirmwareFile(file);
		setError("");
		setStatus("");
		setFlashComplete(false);

		// Read file and calculate CRC32
		const reader = new FileReader();
		reader.onload = (event) => {
			const data = new Uint8Array(event.target.result);
			setFirmwareData(data);
			const crc = crc32(data);
			setCalculatedCRC(crc);
			setStatus(`File loaded: ${file.name} (${data.length} bytes, CRC32: 0x${crc.toString(16).toUpperCase().padStart(8, '0')})`);
		};
		reader.onerror = () => {
			setError("Failed to read file");
		};
		reader.readAsArrayBuffer(file);
	};

	const handleFlashClick = () => {
		if (!firmwareData || !calculatedCRC) {
			setError("Please select a firmware file first");
			return;
		}
		setShowConfirmDialog(true);
	};

	const handleFlashConfirm = async () => {
		setShowConfirmDialog(false);
		setError("");
		setStatus("");
		setFlashComplete(false);
		setBusy(true);

		try {
			if (!Number.isFinite(nodeId) || nodeId < 1 || nodeId > 127) {
				throw new Error("Node ID must be 1-127");
			}

			// Step 1: Write expected CRC32 to 0x2012
			setStatus("Step 1/3: Writing expected CRC32...");
			const crcBytes = new Uint8Array([
				calculatedCRC & 0xFF,
				(calculatedCRC >> 8) & 0xFF,
				(calculatedCRC >> 16) & 0xFF,
				(calculatedCRC >> 24) & 0xFF
			]);
			await canopen.writeSDO(nodeId, 0x2012, 0x00, crcBytes);

			// Step 2: Write firmware data to 0x2011 (segmented SDO transfer)
			setStatus(`Step 2/3: Writing firmware data (${firmwareData.length} bytes)...`);
			await canopen.writeSDO(nodeId, 0x2011, 0x00, firmwareData);

			// Step 3: Read flash control status to check result
			setStatus("Step 3/3: Verifying flash status...");
			// Wait a moment for device to finish verification
			await new Promise(resolve => setTimeout(resolve, 500));
			
			const statusBytes = await canopen.readSDO(nodeId, 0x2010, 0x00);
			const flashStatus = statusBytes[0];

			switch (flashStatus) {
				case 0x10:
					setStatus(`✅ Flash successful! CRC verified. Device ready to jump to new firmware.`);
					setFlashComplete(true);
					break;
				case 0x80:
					throw new Error("Flash failed: CRC mismatch");
				case 0x81:
					throw new Error("Flash failed: Flash write error");
				case 0x82:
					throw new Error("Flash failed: Invalid address/size");
				default:
					setStatus(`⚠️ Flash completed with status: 0x${flashStatus.toString(16).toUpperCase()}`);
					break;
			}
		} catch (e) {
			setError(e.message || String(e));
		} finally {
			setBusy(false);
		}
	};

	const handleJumpToFirmware = async () => {
		setBusy(true);
		setError("");
		try {
			setStatus("Jumping to new firmware...");
			// Write 1 to 0x2019 to trigger jump
			await canopen.writeSDO(nodeId, 0x2019, 0x00, new Uint8Array([0x01]));
			setStatus("✅ Jump command sent! Device should now be running new firmware.");
			setFlashComplete(false);
		} catch (e) {
			setError(`Failed to jump: ${e.message || String(e)}`);
		} finally {
			setBusy(false);
		}
	};

	return (
		<section style={{ padding: 12, borderTop: "1px solid #e5e7eb" }}>
			<h3 style={{ margin: "0 0 12px 0", fontSize: "1.1em", fontWeight: "bold" }}>
				Firmware Flasher
			</h3>

			{/* Node ID Selection */}
			<div style={{ marginBottom: 16 }}>
				<label style={{ display: "flex", alignItems: "center", gap: 6, fontWeight: "500" }}>
					Node ID
					<input
						type="number"
						min={1}
						max={127}
						value={nodeId}
						onChange={(e) => setNodeId(parseInt(e.target.value || "0", 10))}
						disabled={busy}
						style={{ width: 80 }}
					/>
				</label>
			</div>

			{/* File Selection */}
			<div style={{ marginBottom: 16 }}>
				<label style={{ display: "block", marginBottom: 6, fontWeight: "500" }}>
					Firmware File (.bin)
				</label>
				<input
					type="file"
					accept=".bin"
					onChange={handleFileSelect}
					disabled={busy}
					style={{ display: "block" }}
				/>
				{firmwareFile && calculatedCRC && (
					<div style={{ marginTop: 8, fontSize: "0.9em", opacity: 0.8 }}>
						<div>File: {firmwareFile.name}</div>
						<div>Size: {firmwareData.length} bytes</div>
						<div>CRC32: 0x{calculatedCRC.toString(16).toUpperCase().padStart(8, '0')}</div>
					</div>
				)}
			</div>

			{/* Flash Button */}
			<div style={{ marginBottom: 16 }}>
				<button
					onClick={handleFlashClick}
					disabled={busy || !firmwareData}
					style={{
						padding: "8px 16px",
						backgroundColor: !firmwareData ? "#9ca3af" : "#dc2626",
						color: "white",
						border: "none",
						borderRadius: "4px",
						fontWeight: "500",
						cursor: !firmwareData ? "not-allowed" : "pointer"
					}}
				>
					{busy ? "Flashing..." : "Flash Firmware"}
				</button>

				{flashComplete && (
					<button
						onClick={handleJumpToFirmware}
						disabled={busy}
						style={{
							padding: "8px 16px",
							marginLeft: 12,
							backgroundColor: "#10b981",
							color: "white",
							border: "none",
							borderRadius: "4px",
							fontWeight: "500"
						}}
					>
						Jump to Firmware
					</button>
				)}
			</div>

			{/* Confirmation Dialog */}
			{showConfirmDialog && (
				<div style={{
					position: "fixed",
					top: 0,
					left: 0,
					right: 0,
					bottom: 0,
					backgroundColor: "rgba(0,0,0,0.5)",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					zIndex: 1000
				}}>
					<div style={{
						backgroundColor: "white",
						padding: 24,
						borderRadius: 8,
						maxWidth: 400,
						boxShadow: "0 4px 6px rgba(0,0,0,0.1)"
					}}>
						<h3 style={{ margin: "0 0 12px 0", color: "#dc2626" }}>⚠️ Confirm Firmware Flash</h3>
						<p style={{ marginBottom: 16 }}>
							You are about to flash firmware to Node {nodeId}.
							<br /><br />
							<strong>File:</strong> {firmwareFile?.name}<br />
							<strong>Size:</strong> {firmwareData?.length} bytes<br />
							<strong>CRC32:</strong> 0x{calculatedCRC?.toString(16).toUpperCase().padStart(8, '0')}
							<br /><br />
							<strong style={{ color: "#dc2626" }}>
								Do not interrupt this process or the device may become inoperable!
							</strong>
						</p>
						<div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
							<button
								onClick={() => setShowConfirmDialog(false)}
								style={{
									padding: "6px 12px",
									backgroundColor: "#e5e7eb",
									border: "none",
									borderRadius: "4px"
								}}
							>
								Cancel
							</button>
							<button
								onClick={handleFlashConfirm}
								style={{
									padding: "6px 12px",
									backgroundColor: "#dc2626",
									color: "white",
									border: "none",
									borderRadius: "4px",
									fontWeight: "500"
								}}
							>
								Confirm Flash
							</button>
						</div>
					</div>
				</div>
			)}

			{/* Status Messages */}
			{status && (
				<div style={{
					padding: 12,
					backgroundColor: "#f0fdf4",
					border: "1px solid #86efac",
					borderRadius: 4,
					marginBottom: 12
				}}>
					{status}
				</div>
			)}

			{error && (
				<div style={{
					padding: 12,
					backgroundColor: "#fef2f2",
					border: "1px solid #fca5a5",
					borderRadius: 4,
					color: "#b91c1c"
				}}>
					{error}
				</div>
			)}

			{/* Help Text */}
			{!status && !error && (
				<div style={{ fontSize: "0.85em", opacity: 0.6, marginTop: 12 }}>
					<p><strong>Bootloader Flash Process:</strong></p>
					<ul style={{ margin: "8px 0", paddingLeft: 20 }}>
						<li>Select a .bin firmware file (max 64KB)</li>
						<li>Tool automatically calculates CRC32 checksum</li>
						<li>Click "Flash Firmware" and confirm</li>
						<li>Device will erase, write, and verify firmware</li>
						<li>After success, click "Jump to Firmware" to start new firmware</li>
					</ul>
				</div>
			)}
		</section>
	);
}
